import React, { useEffect } from 'react';

import type { CourseModel } from 'Courses/data';

import { useDesktopFullscreen } from './desktop-fullscreen-context';
import { DesktopSwitcher } from './desktop-switcher';
import { SharedFullscreenViewer } from './shared-fullscreen-components';
import { useDesktopSwitcherKeyboard } from './use-desktop-switcher-keyboard';

interface Props {
  course: CourseModel;
}

export const FullscreenDesktopViewer: React.FC<Props> = ({ course }) => {
  const { currentDesktopId, currentDesktop, exitFullscreen, isFullscreen, isBrowserFullscreen } =
    useDesktopFullscreen();

  useDesktopSwitcherKeyboard({
    enabled: isFullscreen
  });

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && currentDesktopId) {
        exitFullscreen();
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [currentDesktopId, exitFullscreen]);

  useEffect(() => {
    if (currentDesktopId && !currentDesktop) {
      exitFullscreen();
    }
  }, [currentDesktopId, currentDesktop, exitFullscreen]);

  const shouldRenderContent = Boolean(currentDesktopId && currentDesktop);
  const isSwitching = Boolean(currentDesktopId && !currentDesktop);

  const currentUser = currentDesktop
    ? {
        displayName: currentDesktop.displayName,
        avatarColor: currentDesktop.avatarColor ?? '',
        avatar: currentDesktop.avatar,
        customAvatar: undefined
      }
    : null;

  if (!currentDesktop || !currentUser) {
    return null;
  }

  return (
    <SharedFullscreenViewer
      isFullscreen={isFullscreen}
      isBrowserFullscreen={isBrowserFullscreen}
      currentDesktop={currentDesktop.desktop}
      currentCourse={course}
      currentUser={currentUser}
      isFreshStartDesktop={currentDesktop.isFreshStartDesktop ?? false}
      isParticipantDesktop={currentDesktop.isParticipantDesktop ?? false}
      isTrainerDesktop={currentDesktop.isTrainerDesktop ?? false}
      exitFullscreen={exitFullscreen}
      shouldRenderContent={shouldRenderContent}
      isSwitching={isSwitching}
      showSwitcher={true}
      switcherComponent={<DesktopSwitcher showSwitcher={true} />}
    />
  );
};
